export const TAG_TRIGGER_EVENTS = [
  'client_tag_added',
  'client_tag_removed',
  'client_tag_changed',
];

export function isTagUsedAsAutomationTrigger(
  tagId: string,
  automationRules: Array<{
    triggerEvent: string;
    conditions: { rules: Array<{ field: string; value: string }> };
  }>,
): boolean {
  return automationRules.some((rule) => {
    return (rule.conditions?.rules ?? []).some((r) => {
      if (r.field !== 'client_tag_ids' && r.field !== 'tag_id') return false;
      try {
        const parsed = JSON.parse(r.value) as string | string[];
        const ids = Array.isArray(parsed) ? parsed : [parsed];
        return ids.includes(tagId);
      } catch {
        return r.value === tagId || r.value.split(',').includes(tagId);
      }
    });
  });
}

export function isFollowUpTemplateUsedInAutomation(
  templateId: string,
  automationRules: Array<{
    enabled?: boolean;
    actions?: Array<{
      type?: string;
      params?: Array<{ key: string; value: string }>;
    }>;
  }>,
  options?: { onlyEnabled?: boolean },
): boolean {
  const rules =
    options?.onlyEnabled === true
      ? automationRules.filter((r) => r.enabled === true)
      : automationRules;
  return rules.some((rule) =>
    (rule.actions ?? []).some((action) => {
      if (action.type !== 'send_template') return false;
      const templateParam = (action.params ?? []).find(
        (p) => p.key === 'follow_up_template_id',
      );
      return templateParam?.value === templateId;
    }),
  );
}

export type AutomationReference = {
  automationRuleId: string;
  name: string;
  location: 'action' | 'trigger';
};

/**
 * Parses automation references from a backend error message.
 * New format: "RESOURCE_IN_USE: Cannot delete [resource]. ... Data: [{automationRuleId, name, location}, ...]"
 * Legacy format: "Cannot delete [resource]. It is currently in use by N automation(s): "Rule 1", "Rule 2". Please..."
 *
 * @param errorMessage - The error message from the backend
 * @returns Array of automation references, or null if parsing fails
 */
export function parseAutomationReferencesFromError(
  errorMessage: string,
): AutomationReference[] | null {
  // Check if error message indicates resource is in use by automations
  if (
    !errorMessage.includes('in use') ||
    !errorMessage.includes('automation')
  ) {
    return null;
  }

  // Try to parse the new structured JSON format first
  const dataMatch = errorMessage.match(/Data:\s*(\[.*?\])(?:\.|$)/s);
  if (dataMatch && dataMatch[1]) {
    try {
      const parsed = JSON.parse(dataMatch[1]) as AutomationReference[];
      if (Array.isArray(parsed) && parsed.length > 0) {
        // Validate the structure
        const validReferences = parsed.filter(
          (ref): ref is AutomationReference =>
            typeof ref === 'object' &&
            ref !== null &&
            'automationRuleId' in ref &&
            'name' in ref &&
            'location' in ref &&
            typeof ref.automationRuleId === 'string' &&
            typeof ref.name === 'string' &&
            (ref.location === 'action' || ref.location === 'trigger'),
        );
        if (validReferences.length > 0) {
          return validReferences;
        }
      }
    } catch {
      // Fall through to legacy parsing
    }
  }

  // Legacy format: Parse automation names from error message
  // The names are quoted strings, potentially separated by commas
  // Example: "Rule 1", "Rule 2", "Welcome, New Client"
  const namesMatch = errorMessage.match(/automation\(s\):\s*(.+?)\.\s*Please/);
  if (!namesMatch || !namesMatch[1]?.trim()) {
    return null;
  }

  // Parse quoted strings from the error message
  // This regex matches quoted strings like "Rule 1" or "Welcome, New Client"
  const quotedStringsRegex = /"([^"]+)"/g;
  const matches = Array.from(namesMatch[1].matchAll(quotedStringsRegex));

  if (matches.length === 0) {
    return null;
  }

  const automationNames = matches
    .map((match) => match[1]?.trim())
    .filter((name): name is string => name !== undefined && name.length > 0);

  if (automationNames.length === 0) {
    return null;
  }

  return automationNames.map((name) => ({
    automationRuleId: '', // ID not available from legacy error message
    name,
    location: 'action' as const,
  }));
}
